import * as THREE from 'three';
import { surface, polygon, curve, createBatch, recess, grille } from './car-geometry.js';

// Original GT coachwork, in metres. All profiles share the same racing envelope.
const DESIGNS = {
  ember:   { length: 4.68, nose: .89, waist: .87, hood: .63, fender: .84, roof: 1.23, front: 1.36, rear: -1.38, cabinFront: .91, cabinRear: -1.15, roofFront: .18, roofRear: -.63, roofWidth: .61, wing: 1.22, lamp: 'swept' },
  thunder: { length: 4.83, nose: .90, waist: .94, hood: .76, fender: .85, roof: 1.27, front: 1.40, rear: -1.38, cabinFront: .49, cabinRear: -1.33, roofFront: -.19, roofRear: -.89, roofWidth: .65, wing: 1.23, lamp: 'blade' },
  eagle:   { length: 4.43, nose: .85, waist: .89, hood: .64, fender: .82, roof: 1.34, front: 1.29, rear: -1.31, cabinFront: .86, cabinRear: -1.49, roofFront: .19, roofRear: -.60, roofWidth: .60, wing: 1.19, lamp: 'oval' },
  venom:   { length: 4.76, nose: .90, waist: .85, hood: .57, fender: .79, roof: 1.17, front: 1.40, rear: -1.39, cabinFront: 1.02, cabinRear: -1.04, roofFront: .22, roofRear: -.57, roofWidth: .59, wing: 1.24, lamp: 'chevron' },
  titan:   { length: 4.75, nose: .93, waist: .95, hood: .73, fender: .88, roof: 1.30, front: 1.40, rear: -1.39, cabinFront: .65, cabinRear: -1.28, roofFront: .05, roofRear: -.82, roofWidth: .68, wing: 1.26, lamp: 'vertical' },
};
const WHEEL_RADIUS = .338;
const clamp = THREE.MathUtils.clamp;
const lerp = THREE.MathUtils.lerp;
const smooth = t => t * t * (3 - 2 * t);

function numberTexture(number) {
  const canvas = document.createElement('canvas'); canvas.width = 256; canvas.height = 192;
  const ctx = canvas.getContext('2d'); ctx.fillStyle = '#f5f3e9'; ctx.fillRect(0, 0, 256, 192);
  ctx.fillStyle = '#101318'; ctx.font = 'italic 900 154px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(number, 122, 101);
  ctx.fillStyle = '#151921'; ctx.fillRect(0, 0, 256, 9);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 8; return texture;
}

function carbonTexture() {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d'); ctx.fillStyle = '#1b1e22'; ctx.fillRect(0, 0, 64, 64);
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) {
    ctx.fillStyle = (x + y) % 2 ? '#34373b' : '#22262a'; ctx.fillRect(x * 8, y * 8, 7, 7);
    ctx.fillStyle = '#42464a'; ctx.fillRect(x * 8, y * 8, (x + y) % 2 ? 1 : 6, (x + y) % 2 ? 6 : 1);
  }
  const texture = new THREE.CanvasTexture(canvas); texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.repeat.set(6, 6); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 8; return texture;
}

function materials(car) {
  const paint = new THREE.Color(car.color).multiplyScalar(car.id === 'titan' ? .89 : .78);
  return {
    paint: new THREE.MeshPhysicalMaterial({ color: paint, metalness: .48, roughness: .21, clearcoat: 1, clearcoatRoughness: .075, envMapIntensity: 1.15, side: THREE.DoubleSide }),
    accent: new THREE.MeshStandardMaterial({ color: car.accent, metalness: .5, roughness: .24, side: THREE.DoubleSide }),
    carbon: new THREE.MeshPhysicalMaterial({ color: '#252a30', map: carbonTexture(), metalness: .20, roughness: .36, clearcoat: .45, side: THREE.DoubleSide }),
    black: new THREE.MeshStandardMaterial({ color: '#030506', roughness: .93, side: THREE.DoubleSide }),
    mesh: new THREE.MeshStandardMaterial({ color: '#343b41', metalness: .6, roughness: .44 }),
    glass: new THREE.MeshPhysicalMaterial({ color: '#18242f', metalness: .12, roughness: .075, clearcoat: 1, clearcoatRoughness: .03, transparent: true, opacity: .88, depthWrite: false, side: THREE.DoubleSide, envMapIntensity: 1.2 }),
    lens: new THREE.MeshPhysicalMaterial({ color: '#101822', metalness: .18, roughness: .055, clearcoat: 1, clearcoatRoughness: .02, side: THREE.DoubleSide }),
    tire: new THREE.MeshStandardMaterial({ color: '#101114', roughness: .94 }),
    rim: new THREE.MeshStandardMaterial({ color: car.id === 'ember' ? '#4d4335' : '#343d46', metalness: .9, roughness: .25 }),
    metal: new THREE.MeshStandardMaterial({ color: '#848d97', metalness: 1, roughness: .26 }),
    brake: new THREE.MeshStandardMaterial({ color: car.id === 'thunder' ? '#cb2831' : '#ba7d29', metalness: .55, roughness: .4 }),
    led: new THREE.MeshBasicMaterial({ color: '#e1eeff', toneMapped: false, side: THREE.DoubleSide }),
    tail: new THREE.MeshBasicMaterial({ color: '#ec1534', toneMapped: false, side: THREE.DoubleSide }),
    number: new THREE.MeshStandardMaterial({ map: numberTexture(car.number), roughness: .35, metalness: .15, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2 }),
  };
}

function coordinates(d) {
  const half = d.length / 2;
  function width(z) {
    const front = Math.exp(-(((z - d.front) / .53) ** 2)), rear = Math.exp(-(((z - d.rear) / .62) ** 2));
    const end = smooth(clamp((Math.abs(z) - 1.66) / (half - 1.66), 0, 1));
    return lerp(d.waist + (1.035 - d.waist) * Math.max(front, rear), z > 0 ? d.nose : .89, end);
  }
  function top(x, z) {
    const u = clamp(Math.abs(x) / width(z), 0, 1);
    const nose = smooth(clamp((z - d.front + .05) / (half - d.front + .05), 0, 1));
    const hood = d.hood - .14 * nose + .015 * Math.cos(z * 1.1);
    const bump = Math.max(Math.exp(-(((z - d.front) / .62) ** 2)), Math.exp(-(((z - d.rear) / .72) ** 2)));
    return hood + ((d.fender - hood) * bump + .02) * Math.sin(u * Math.PI / 2) ** 4 - .035 * u ** 14;
  }
  function edge(x, z) { return z - Math.sign(z) * .20 * (Math.abs(x) / width(z)) ** 4 * smooth(clamp((Math.abs(z) - half + .65) / .65, 0, 1)); }
  function arch(z) {
    let y = .16;
    for (const axle of [d.front, d.rear]) if (Math.abs(z - axle) < .365) y = Math.max(y, .338 + Math.sqrt(.365 ** 2 - (z - axle) ** 2));
    return y;
  }
  return { half, width, top, edge, arch };
}

function body(batch, d, c, detail) {
  const rows = [], segments = detail === 'hero' ? 110 : 72;
  for (let i = 0; i <= segments; i++) {
    const z = -c.half + d.length * i / segments, w = c.width(z), upperY = c.top(w * .965, z), lowerY = Math.min(c.arch(z), upperY - .026);
    const archBlend = smooth(clamp((lowerY - .16) / .43, 0, 1));
    const halfRow = [];
    for (let j = 0; j <= 16; j++) { const x = w * .965 * j / 16; halfRow.push([x, c.top(x, z), c.edge(x, z)]); }
    for (let j = 1; j <= 10; j++) {
      const t = j / 10;
      const x = w * (.965 + .035 * Math.sin(t * Math.PI) - .095 * t * t * (1 - archBlend)) + .006 * archBlend * t;
      const y = lerp(upperY, lowerY, t);
      halfRow.push([x, y, c.edge(x, z)]);
    }
    rows.push([...halfRow.slice(1).reverse().map(([x, y, z]) => [-x, y, z]), ...halfRow]);
  }
  batch.add('paint', surface(rows));
  batch.box('black', [1.65, .09, d.length - .35], [0, .13, 0]);
  for (const side of [-1, 1]) {
    for (const axle of [d.front, d.rear]) {
      const arc = Array.from({ length: 36 }, (_, i) => {
        const a = -.10 + (Math.PI + .20) * i / 35, z = axle + .367 * Math.cos(a);
        return [side * (c.width(z) * .978 + .010), .337 + .367 * Math.sin(a), z];
      });
      batch.tube('paint', arc, .018, 40, 8);
      batch.tube('black', arc.map(([x,y,z]) => [x-side*.012,y-.014,z]), .011, 32, 6);
    }
    const sill = [[side * .90, .16, 1.02], [side * .96, .12, .63], [side * .98, .11, -.94], [side * 1.00, .16, -1.06]];
    batch.tube('carbon', sill, .042, 24, 8);
    batch.tube('accent', sill.map(([x,y,z])=>[x+side*.010,y+.027,z]), .009, 24, 6);
    const intakeZ = d.cabinRear + .20;
    const intake = [[side*.91,.67,intakeZ+.12],[side*.95,.36,intakeZ+.05],[side*1.007,.43,intakeZ-.37],[side*1.013,.79,intakeZ-.43]];
    recess(batch, intake, [-side*.12,0,-.08], 'paint');
    batch.tube('carbon', [[side*.88,.70,.49],[side*.91,.47,.46],[side*.87,.23,.23],[side*.92,.23,-.67],[side*.98,.64,-.70]], .005, 30, 5);
    batch.box('carbon', [.018,.017,.14], [side*.912,.65,-.44]);
    const door = [];
    for (let row = 0; row <= 7; row++) {
      const z = -.33 + row / 7 * .46;
      door.push(Array.from({ length: 6 }, (_, col) => {
        const y = .30 + col / 5 * .29, t = clamp((c.top(c.width(z)*.965,z)-y)/(c.top(c.width(z)*.965,z)-.16),0,1);
        const x = c.width(z)*(.965+.035*Math.sin(t*Math.PI)-.095*t*t)+.007;
        return [side*x,y,z];
      }));
    }
    const decal = surface(door, side < 0); const uv = decal.attributes.uv;
    for(let i=0;i<uv.count;i++) { const u=uv.getX(i),v=uv.getY(i); uv.setXY(i,side>0?1-v:v,u); }
    batch.add('number', decal);
  }
}

function fascia(batch, d, c) {
  const z = c.half;
  const upper = [];
  for (let row = 0; row <= 8; row++) {
    const t = row / 8;
    upper.push(Array.from({length:33},(_,i)=>{
      const x=(i-16)/16*d.nose, edge=c.edge(x,z);
      return [x,lerp(.465,c.top(x,z),t),edge+.013*Math.sin(t*Math.PI)];
    }));
  }
  batch.add('paint',surface(upper,true));
  const mouth=[[-.58,.225,z+.005],[.58,.225,z+.005],[.66,.46,z-.017],[-.66,.46,z-.017]];
  recess(batch,mouth,[0,.012,-.18],'carbon'); grille(batch,1.03,.19,.262,z-.11);
  for(const side of [-1,1]) {
    const vent=[[side*.66,.22,z-.01],[side*.91,.225,z-.19],[side*.94,.43,z-.20],[side*.72,.465,z-.03]];
    recess(batch,vent,[0,0,-.14],'carbon');
    batch.add('paint',polygon([[side*.60,.20,z+.02],[side*.67,.20,z-.02],[side*.74,.47,z-.03],[side*.66,.48,z-.009]]));
    batch.add('paint',polygon([[side*.90,.18,z-.17],[side*(d.nose+.065),.19,z-.31],[side*d.nose,.57,z-.29],[side*.94,.44,z-.19]]));
    batch.tube('paint',[[side*.14,.48,z+.015],[side*.56,.48,z-.008],[side*.81,.48,z-.11],[side*.96,.55,z-.30]],.029,30,8);
    batch.add('carbon',polygon([[side*.78,.24,z-.24],[side*1.02,.24,z-.34],[side*1.03,.28,z-.68],[side*.89,.29,z-.53]]));
  }
  const splitter=[];
  for(let r=0;r<3;r++) splitter.push(Array.from({length:33},(_,i)=>{
    const x=(i-16)/16*1.015; return [x,.115+.007*r,c.edge(x,z)+.12-r*.21];
  }));
  batch.add('carbon',surface(splitter));
  batch.tube('accent',[[-.94,.128,z-.05],[-.53,.123,z+.105],[.53,.123,z+.105],[.94,.128,z-.05]],.007,30,5);
  for(const x of [-.50,.50]) batch.rod('metal',[x,.15,z+.05],[x*.90,.32,z-.10],.005,6);
  batch.add('paint',polygon([[-.59,.18,z+.03],[.59,.18,z+.03],[.60,.218,z+.019],[-.60,.218,z+.019]]));
  const rear=-c.half;
  batch.add('paint',surface([Array.from({length:29},(_,i)=>{const x=(i-14)/14*.88;return[x,.27,c.edge(x,rear)];}),Array.from({length:29},(_,i)=>{const x=(i-14)/14*.89;return[x,.34,c.edge(x,rear)];})]));
  for (const side of [-1,1]) batch.add('paint',polygon([[side*.84,.30,rear],[side*.96,.31,rear+.17],[side*.96,.66,rear+.12],[side*.80,.66,rear+.01]]));
  recess(batch,[[-.84,.34,rear-.025],[.84,.34,rear-.025],[.79,.63,rear-.02],[-.79,.63,rear-.02]],[0,0,.13],'carbon');
  grille(batch,1.35,.16,.39,rear+.072,-1);
  batch.add('carbon',polygon([[-1.02,.095,rear-.16],[1.02,.095,rear-.16],[.90,.23,rear+.27],[-.90,.23,rear+.27]]));
  for(const x of [-.77,-.50,-.24,0,.24,.50,.77]) batch.add('carbon',polygon([[x,.09,rear-.18],[x,.25,rear+.10],[x,.28,rear+.38],[x,.12,rear+.30]]));
  for(const side of [-1,1]) {
    const x=side*.60;
    batch.add('metal',new THREE.TorusGeometry(.067,.009,10,28),[x,.29,rear-.04]);
    batch.add('black',new THREE.CircleGeometry(.061,28),[x,.29,rear-.045],[0,Math.PI,0]);
    if(d.lamp==='swept'||d.lamp==='oval') {
      batch.add('lens',new THREE.CylinderGeometry(.131,.131,.032,32),[side*.74,.656,rear-.016],[Math.PI/2,0,0]);
      batch.add('tail',new THREE.TorusGeometry(.099,.012,8,32),[side*.74,.656,rear-.038]);
      batch.add('black',new THREE.CircleGeometry(.074,24),[side*.74,.656,rear-.052],[0,Math.PI,0]);
    } else {
      batch.tube('lens',[[side*.40,.67,rear-.018],[side*.78,.67,rear+.005],[side*.91,.70,rear+.065]],.048,20,8);
      batch.tube('tail',[[side*.42,.67,rear-.054],[side*.76,.67,rear-.033],[side*.89,.70,rear+.026]],.014,20,6);
    }
  }
  batch.add('number',new THREE.PlaneGeometry(.29,.19),[0,.52,rear-.032],[0,Math.PI,0]);
}

function cabin(batch,d,c) {
  const base=d.hood+.095, width=d.roofWidth;
  const front=[];
  for(let row=0;row<=14;row++) {
    const t=row/14;
    front.push(Array.from({length:25},(_,i)=>{
      const u=(i-12)/12, w=lerp(.735,width,t);
      return [u*w,lerp(base,d.roof-.035,t)-.058*u*u*t,lerp(d.cabinFront,d.roofFront,t)-.13*u*u*(1-t)+.035*Math.sin(t*Math.PI)];
    }));
  }
  batch.add('glass',surface(front,true));
  batch.add('paint',surface([
    front[0].map(([x,y,z])=>[x,c.top(x,z+.055)+.002,z+.055]),
    front[0],
  ]));
  const roof=[];
  for(let row=0;row<=18;row++) {
    const t=row/18,z=lerp(d.roofFront,d.roofRear,t);
    roof.push(Array.from({length:25},(_,i)=>{const u=(i-12)/12;return[u*width*(1-.07*Math.sin(t*Math.PI)),d.roof-.035-.058*u*u+.020*Math.sin(t*Math.PI),z];}));
  }
  batch.add('paint',surface(roof,true));
  const rear=[];
  for(let row=0;row<=12;row++) {
    const t=row/12;
    rear.push(Array.from({length:21},(_,i)=>{const u=(i-10)/10;return[u*lerp(width,.70,t),lerp(d.roof-.035,base+.005,t)-.058*u*u*(1-t),lerp(d.roofRear,d.cabinRear,t)+.09*u*u*t];}));
  }
  batch.add('glass',surface(rear,true));
  batch.add('paint',surface([rear[rear.length-1],rear[rear.length-1].map(([x,y,z])=>[x,c.top(x,z-.09)+.004,z-.09])]));
  for(const side of [-1,1]) {
    const lowerFront=[side*.736,base,d.cabinFront-.13],upperFront=[side*width,d.roof-.093,d.roofFront],upperRear=[side*width,d.roof-.093,d.roofRear],lowerRear=[side*.70,base+.005,d.cabinRear+.09];
    const sideRows=[];
    for(let row=0;row<=18;row++) {
      const t=row/18;
      sideRows.push(Array.from({length:11},(_,i)=>{
        const v=i/10,top=[side*width*(1-.07*Math.sin(t*Math.PI)),d.roof-.093+.020*Math.sin(t*Math.PI),lerp(d.roofFront,d.roofRear,t)];
        const bottom=[side*lerp(.736,.70,t),base+.005*t,lerp(d.cabinFront-.13,d.cabinRear+.09,t)];
        return top.map((value,axis)=>lerp(value,bottom[axis],v)+(axis===0?side*.025*Math.sin(v*Math.PI):0));
      }));
    }
    batch.add('glass',surface(sideRows,side<0));
    batch.tube('paint',curve([lowerFront,[side*.69,base+.23,d.cabinFront-.42],upperFront]),.034,30,10);
    batch.tube('paint',[upperFront,[side*width*.955,d.roof-.074,(d.roofFront+d.roofRear)/2],upperRear],.031,28,10);
    batch.tube('paint',[upperRear,[side*.69,base+.24,d.cabinRear+.36],lowerRear],.065,24,12);
    batch.tube('paint',[lowerFront,[side*.737,base-.015,(d.cabinFront+d.cabinRear)/2],lowerRear],.026,24,8);
    batch.tube('black',[[side*width*.976,d.roof-.090,d.roofRear+.18],[side*.728,base,d.roofRear+.02]],.011,8,6);
    batch.add('paint',polygon([upperRear,lowerRear,[side*.99,d.fender-.015,d.rear+.10],[side*.87,d.fender,d.rear+.40]]));
    batch.rod('carbon',[side*.73,base+.07,d.cabinFront-.17],[side*.96,base+.125,d.cabinFront-.02],.022,10);
    batch.add('paint',new THREE.SphereGeometry(1,24,14),[side*.995,base+.138,d.cabinFront-.035],[0,side*.17,0],[.120,.058,.074]);
    batch.add('glass',new THREE.SphereGeometry(1,20,10),[side*.999,base+.135,d.cabinFront-.089],[0,side*.17,0],[.091,.040,.012]);
  }
  batch.tube('black',[[-width*.97,d.roof-.085,d.roofFront+.009],[0,d.roof-.019,d.roofFront+.009],[width*.97,d.roof-.085,d.roofFront+.009]],.020,24,8);
  batch.tube('carbon',[[.15,base+.015,d.cabinFront+.013],[-.10,base+.15,d.cabinFront-.23],[-.20,base+.31,d.cabinFront-.39]],.007,12,6);
  batch.box('black',[1.25,.18,1.22],[0,base-.11,-.18]);
  batch.box('carbon',[1.14,.13,.32],[0,base+.045,d.cabinFront-.24],[-.16,0,0]);
  for(const side of [-1,1]) {
    batch.add('black',new THREE.SphereGeometry(1,16,12),[side*.29,base+.16,-.45],[.12,0,0],[.22,.26,.13]);
    batch.tube('mesh',[[side*.56,base-.02,-.64],[side*.54,d.roof-.15,-.58],[0,d.roof-.12,-.62],[-side*.54,d.roof-.15,-.58]],.016,18,6);
  }
  batch.add('carbon',new THREE.TorusGeometry(.123,.015,8,20),[-.30,base+.15,d.cabinFront-.35],[-.50,0,0]);
}

function lamps(batch,d,c) {
  const front=c.half;
  for(const side of [-1,1]) {
    const shape=d.lamp;
    let outline;
    if(shape==='oval') {
      outline=Array.from({length:24},(_,i)=>{const a=i/24*Math.PI*2;const x=side*(.70+Math.cos(a)*.153),z=front-.31+Math.sin(a)*.226;return[x,c.top(x,z)+.013,z];});
    } else {
      const points=shape==='blade'?[[.49,front-.22],[.88,front-.34],[.89,front-.64],[.58,front-.51]]:shape==='vertical'?[[.68,front-.12],[.88,front-.25],[.87,front-.67],[.62,front-.56]]:shape==='chevron'?[[.52,front-.19],[.89,front-.44],[.91,front-.82],[.65,front-.61]]:[[.51,front-.17],[.84,front-.29],[.90,front-.77],[.62,front-.55]];
      const corners=points.map(([x,z])=>new THREE.Vector3(side*x,c.top(x,z)+.013,z));
      outline=corners.flatMap((point,index)=>{
        const before=point.clone().lerp(corners[(index+corners.length-1)%corners.length],.14);
        const after=point.clone().lerp(corners[(index+1)%corners.length],.14);
        return new THREE.QuadraticBezierCurve3(before,point,after).getPoints(6).map(p=>p.toArray());
      });
    }
    batch.add('lens',polygon(outline)); batch.tube('carbon',outline,.012,36,8,true);
    const center=new THREE.Vector3();outline.forEach(p=>center.add(new THREE.Vector3(...p)));center.multiplyScalar(1/outline.length);
    if(shape==='oval') {
      const ring=outline.map(p=>p.map((value,axis)=>lerp(center.getComponent(axis),value,.79)+(axis===1?.009:0)));
      batch.tube('led',ring,.006,36,6,true);
      for(const offset of [-.055,.055]) batch.add('metal',new THREE.SphereGeometry(1,16,10),[center.x+offset,center.y+.014,center.z],[0,0,0],[.044,.011,.054]);
    } else {
      const frontPoints=outline.slice(0,Math.floor(outline.length*.43)).map(([x,y,z])=>[x,y+.015,z]);
      batch.tube('led',frontPoints,.007,22,6);
      for(let i=0;i<3;i++) {
        const z=front-.33-i*.105,x=side*(.68+i*.035);
        batch.add('metal',new THREE.SphereGeometry(1,14,8),[x,c.top(x,z)+.026,z],[0,0,0],[.047,.012,.040]);
        batch.add('led',new THREE.SphereGeometry(1,12,8),[x,c.top(x,z)+.034,z+.003],[0,0,0],[.025,.005,.025]);
      }
    }
  }
}

function details(batch,d,c) {
  const hoodZ=lerp(d.cabinFront,c.half,.47);
  const decal=surface(Array.from({length:9},(_,i)=>{
    const z=hoodZ-.18+i/8*.36;return[-.235,.235].map(x=>[x,c.top(x,z)+.008,z]);
  }));
  const uv=decal.attributes.uv;for(let i=0;i<uv.count;i++)uv.setXY(i,1-uv.getX(i),uv.getY(i));batch.add('number',decal);
  for(const side of [-1,1]) {
    const outline=[[side*.20,hoodZ+.28],[side*.36,hoodZ+.23],[side*.39,hoodZ-.14],[side*.27,hoodZ-.22]].map(([x,z])=>[x,c.top(x,z)+.008,z]);
    batch.add('black',polygon(outline));batch.tube('carbon',outline,.008,16,6,true);
    for(let i=0;i<5;i++) {
      const z=hoodZ-.11+i*.060;
      batch.rod('carbon',[side*.27,c.top(side*.27,z)+.016,z],[side*.38,c.top(side*.38,z)+.016,z+.02],.010,6);
    }
    const stripe=[[side*.22,c.half-.04],[side*.29,hoodZ-.30],[side*.34,d.cabinFront+.03]].map(([x,z])=>[x,c.top(x,z)+.008,z]);
    batch.tube('accent',stripe,.008,24,6);
    for(let i=0;i<5;i++) {
      const z=d.front-.18+i*.073,x=side*.935;
      batch.rod('black',[x-side*.045,c.top(x,z)+.008,z],[x+side*.035,c.top(x,z+.008)+.008,z+.025],.012,6);
    }
  }
  for(let i=0;i<7;i++) {
    const z=d.cabinRear-.17-i*.067;
    batch.rod('black',[-.43,c.top(-.43,z)+.009,z],[.43,c.top(.43,z)+.009,z],.017,8);
  }
  const wingZ=-c.half+.22,wing=[];
  for(let row=0;row<=26;row++) {
    const x=-1.07+2.14*row/26,sweep=.10*(Math.abs(x)/1.07)**2;
    wing.push(Array.from({length:19},(_,i)=>{
      const t=i/18;return[x,d.wing+.042*Math.sin(Math.PI*t)-.022*t,wingZ+.25-.49*t+sweep];
    }));
  }
  batch.add('carbon',surface(wing));
  const underside=wing.map(row=>row.map(([x,y,z])=>[x,y-.045,z]));
  batch.add('carbon',surface(underside,true));
  for(const edge of [0,18]) batch.add('carbon',surface(wing.map((row,i)=>[row[edge],underside[i][edge]])));
  for(const side of [-1,1]) {
    batch.add('carbon',polygon([[side*1.075,d.wing-.065,wingZ-.20],[side*1.075,d.wing+.125,wingZ-.14],[side*1.075,d.wing+.11,wingZ+.35],[side*1.075,d.wing-.06,wingZ+.31]]));
    batch.tube('accent',[[side*1.078,d.wing+.10,wingZ-.12],[side*1.078,d.wing+.085,wingZ+.27]],.007,5,5);
    batch.tube('carbon',[[side*.55,c.top(.55,wingZ+.33),wingZ+.33],[side*.55,d.wing-.19,wingZ+.34],[side*.55,d.wing+.035,wingZ+.24],[side*.55,d.wing+.047,wingZ+.07]],.027,20,8);
  }
}

function wheels(root,palette,d,detail) {
  const segments=detail==='hero'?48:32;
  const tireProfile=[[-.105,.250],[-.129,.267],[-.142,.298],[-.130,.327],[-.105,.336],[-.070,.338],[.070,.338],[.105,.336],[.130,.327],[.142,.298],[.129,.267],[.105,.250]];
  const tire=surface(Array.from({length:segments+1},(_,i)=>{
    const a=i/segments*Math.PI*2;return tireProfile.map(([x,r])=>[x,Math.cos(a)*r,Math.sin(a)*r]);
  }));
  const template=new THREE.Group(),batch=createBatch(template,palette);
  batch.add('tire',tire);
  batch.add('rim',new THREE.CylinderGeometry(.251,.251,.215,segments,1,true),[0,0,0],[0,0,Math.PI/2]);
  for(const side of [-1,1]) {
    const x=side*.132;
    batch.add('rim',new THREE.TorusGeometry(.258,.011,8,segments),[x,0,0],[0,Math.PI/2,0]);
    batch.add('metal',new THREE.TorusGeometry(.251,.003,6,segments),[x+side*.004,0,0],[0,Math.PI/2,0]);
    batch.add('mesh',new THREE.CylinderGeometry(.216,.216,.009,segments),[side*.085,0,0],[0,0,Math.PI/2]);
    for(let i=0;i<24;i++) {
      const a=i/24*Math.PI*2,r=i%2?.187:.173;
      batch.add('black',new THREE.CircleGeometry(.0055,6),[side*.091,r*Math.cos(a),r*Math.sin(a)],[0,side*Math.PI/2,0]);
    }
    for(let spoke=0;spoke<5;spoke++) {
      const angle=spoke/5*Math.PI*2;
      for(const fork of [-1,1]) {
        const p=(r,a,depth=0)=>[x+side*depth,Math.cos(a)*r,Math.sin(a)*r];
        const outline=[p(.063,angle-.13,.006),p(.16,angle+fork*.04,.013),p(.247,angle+fork*.15,.006),p(.247,angle+fork*.15+.08,.006),p(.15,angle+fork*.10+.09,.013),p(.063,angle+.17,.006)];
        batch.add('rim',polygon(outline));
        batch.tube('rim',outline,.006,outline.length*2,5,true);
      }
    }
    batch.add('rim',new THREE.CylinderGeometry(.063,.063,.022,12),[x+side*.012,0,0],[0,0,Math.PI/2]);
    batch.add('brake',new THREE.CylinderGeometry(.030,.030,.028,6),[x+side*.026,0,0],[0,0,Math.PI/2]);
    batch.add('metal',new THREE.TorusGeometry(.034,.003,6,20),[x+side*.042,0,0],[0,Math.PI/2,0]);
  }
  batch.finish();root.userData.wheels=[];root.userData.frontWheels=[];
  for(const z of [d.front,d.rear])for(const side of [-1,1]) {
    const pivot=new THREE.Group();pivot.position.set(side*.895,.340,z);
    const spin=new THREE.Object3D();pivot.add(spin);root.add(pivot);root.userData.wheels.push(spin);
    if(z===d.front)root.userData.frontWheels.push(pivot);
    const caliper=new THREE.Mesh(new THREE.BoxGeometry(.048,.15,.074),palette.brake);caliper.position.set(side*.105,.01,-.16);pivot.add(caliper);
  }
  root.userData.wheelMeshes=template.children.map(part=>{
    const instances=new THREE.InstancedMesh(part.geometry,part.material,4);
    instances.name=`wheel-${part.name}`;instances.castShadow=part.castShadow;instances.receiveShadow=true;
    instances.instanceMatrix.setUsage(THREE.DynamicDrawUsage);root.add(instances);return instances;
  });
  root.userData.wheelMatrix=new THREE.Matrix4();
  updateWheelInstances(root);
  for(const instances of root.userData.wheelMeshes) instances.computeBoundingSphere();
}

function updateWheelInstances(group) {
  const matrix=group.userData.wheelMatrix;
  for(let i=0;i<group.userData.wheels.length;i++) {
    const wheel=group.userData.wheels[i];
    wheel.parent.updateMatrix();wheel.updateMatrix();matrix.multiplyMatrices(wheel.parent.matrix,wheel.matrix);
    for(const instances of group.userData.wheelMeshes) instances.setMatrixAt(i,matrix);
  }
  for(const instances of group.userData.wheelMeshes) instances.instanceMatrix.needsUpdate=true;
}

export function buildCar(car,{detail='hero'}={}) {
  const d=DESIGNS[car.shape]||DESIGNS[car.id]||DESIGNS.ember,root=new THREE.Group();
  root.name=`gt-${car.id}`;root.userData.carId=car.id;root.userData.wheelRadius=WHEEL_RADIUS;
  const palette=materials(car),batch=createBatch(root,palette),c=coordinates(d);
  body(batch,d,c,detail);fascia(batch,d,c);cabin(batch,d,c);lamps(batch,d,c);details(batch,d,c);batch.finish();wheels(root,palette,d,detail);
  root.userData.animationTime=null;return root;
}

export function animateCar(group,{speed=0,steering=0,braking=false,time=0}={}) {
  const previous=group.userData.animationTime;
  const delta=previous===null||previous===undefined?0:clamp(time-previous,0,.10);group.userData.animationTime=time;
  const distance=Number.isFinite(speed)?speed*delta:0;
  for(const wheel of group.userData.wheels||[])wheel.rotation.x=(wheel.rotation.x+distance/(group.userData.wheelRadius||WHEEL_RADIUS))%(Math.PI*2);
  for(const wheel of group.userData.frontWheels||[])wheel.rotation.y=clamp(Number.isFinite(steering)?steering:0,-.45,.45);
  const target=braking?1:0, strength=group.userData.brakeStrength||0;
  group.userData.brakeStrength=strength+(target-strength)*(1-Math.exp(-delta*18));
  for(const [material,intensity] of group.userData.brakeLights||[]) material.emissiveIntensity=intensity*(1+group.userData.brakeStrength*3);
  if(group.userData.wheelMeshes) updateWheelInstances(group);
}
